import { useState, useEffect } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { getPageByRoute } from '../../services/pagesService';
import PageBlocks from './PageBlocks';
import Header from './Header';
import Footer from './Footer';

/**
 * La page de la base si elle est publiée, l'ancienne sinon.
 *
 * ========================================================================
 * POURQUOI CE COMPOSANT EXISTE
 * ========================================================================
 *
 * Faire servir une page de la base demandait jusqu'ici de modifier App.jsx,
 * reconstruire et redéployer — un geste de développeur pour une décision
 * d'éditeur. Et le basculement était à sens unique : `/install` a été basculé
 * avant que le rendu par blocs n'égale la page soignée, et revenir en arrière
 * demandait un nouveau déploiement.
 *
 * Désormais, LA PUBLICATION EST L'INTERRUPTEUR :
 *
 *   page publiée sur cette route  →  elle s'affiche
 *   page dépubliée, ou absente    →  l'ancienne reprend la main
 *
 * On peut donc publier, comparer les deux rendus, et dépublier si le résultat
 * ne convient pas — le visiteur retrouve la page d'origine immédiatement.
 *
 * ========================================================================
 * CE QUE L'ON AFFICHE PENDANT L'INTERROGATION
 * ========================================================================
 *
 * Le repli, pas un écran de chargement.
 *
 * Un « chargement… » ferait clignoter toutes les pages concernées à chaque
 * visite, pour une bascule qui reste l'exception. Afficher d'emblée
 * l'ancienne page ne coûte rien quand elle gagne — le cas courant — et
 * produit un remplacement bref quand la base l'emporte.
 *
 * ========================================================================
 * UNE PANNE DE L'API NE DOIT RIEN CASSER
 * ========================================================================
 *
 * Si l'appel échoue, on garde le repli. Une page de contenu indisponible vaut
 * mieux qu'une page blanche, et c'est la raison d'avoir gardé les composants
 * d'origine plutôt que de les supprimer.
 */
export default function PageOuRepli({ route, repli: Repli, propsRepli = {} }) {
    const { language } = useTranslation();
    const [page, setPage] = useState(null);

    useEffect(() => {
        let annule = false;

        const resoudre = async () => {
            try {
                const trouvee = await getPageByRoute(route, language);
                if (!annule) setPage(trouvee);
            } catch (e) {
                // On garde le repli : voir l'en-tête.
                if (!annule) setPage(null);
            }
        };

        resoudre();
        return () => { annule = true; };
    }, [route, language]);

    if (!page) {
        return <Repli {...propsRepli} />;
    }

    // LE TITRE DE LA PAGE N'EST PAS AFFICHE DEUX FOIS.
    //
    // Un bloc « hero » porte deja le titre de couverture. L'ajouter au-dessus
    // donnait « Installer l'application » suivi de « Installez l'application
    // Cabaza » — deux titres qui disent la meme chose, dont l'un vient du
    // champ `title` et l'autre du contenu.
    //
    // Le champ `title` sert alors ce a quoi il est fait : l'onglet du
    // navigateur, les listes de l'admin, le referencement. Sans hero, il
    // reste affiche, sans quoi la page commencerait sans en-tete.
    const aUnHero = Array.isArray(page.blocs) && page.blocs.some(b => b?.type === 'hero');

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header {...propsRepli} />
            <main className="flex-grow max-w-4xl mx-auto px-4 py-12 w-full">
                <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
                    {!aUnHero && (
                        <h1 className="text-3xl font-bold text-gray-900 mb-8">{page.title}</h1>
                    )}
                    <PageBlocks blocs={page.blocs} />
                </div>
            </main>
            <Footer />
        </div>
    );
}
